// ============================================
// APPLICATION CONSTANTS
// ============================================

export const APP_NAME = 'FL Smartech';
export const COMPANY_FULL_NAME = 'FL Smartech Private Limited';
export const SERVICE_FEE = 99; // ₹99
export const CURRENCY = 'INR';
export const EXPERT_YEARS = 20;
export const SUPPORT_EMAIL = 'flsmartechpvtltd@gmail.com';

// ============================================
// FORM OPTIONS
// ============================================

export const DEVICE_INTENT_OPTIONS = [
  { value: 'laptop', label: 'Laptop', description: 'Portable, work from anywhere' },
  { value: 'desktop', label: 'Desktop', description: 'More power, fixed location' },
  { value: 'not_sure', label: 'Not Sure', description: 'Help me decide' },
] as const;

export const PERSONA_OPTIONS = [
  { value: 'student', label: 'Student', icon: '🎓' },
  { value: 'professional', label: 'Working Professional', icon: '💼' },
  { value: 'freelancer', label: 'Freelancer', icon: '🚀' },
  { value: 'business', label: 'Business Owner', icon: '🏢' },
  { value: 'home', label: 'Home User', icon: '🏠' },
] as const;

export const PRIMARY_USAGE_OPTIONS = [
  { value: 'office_work', label: 'Office Work', description: 'Documents, email, spreadsheets' },
  { value: 'programming', label: 'Programming', description: 'Coding, development, testing' },
  { value: 'video_editing', label: 'Video Editing', description: 'Premiere, DaVinci, Final Cut' },
  { value: 'graphic_design', label: 'Graphic Design', description: 'Photoshop, Illustrator, Figma' },
  { value: 'gaming', label: 'Gaming', description: 'PC games, high FPS gaming' },
  { value: 'web_browsing', label: 'Web Browsing', description: 'Internet, streaming, social media' },
  { value: 'data_analysis', label: 'Data Analysis', description: 'Excel, Python, databases' },
  { value: 'content_creation', label: 'Content Creation', description: 'YouTube, podcasts, streaming' },
  { value: 'online_classes', label: 'Online Classes', description: 'Zoom, Meet, online learning' },
  { value: 'accounting', label: 'Accounting / Tally', description: 'Tally, GST, billing software' },
  { value: 'stock_trading', label: 'Stock Trading', description: 'Trading platforms, charting' },
  { value: 'other', label: 'Other', description: 'Something else' },
] as const;

export const DAILY_HOURS_OPTIONS = [
  { value: 'less_than_2', label: 'Less than 2 hours', description: 'Light usage' },
  { value: '2_to_4', label: '2-4 hours', description: 'Moderate usage' },
  { value: '4_to_8', label: '4-8 hours', description: 'Heavy usage' },
  { value: 'more_than_8', label: 'More than 8 hours', description: 'Power user' },
] as const;

export const BUDGET_OPTIONS = [
  { value: 'under_30k', label: 'Under ₹30,000', tier: 'entry' },
  { value: '30k_to_50k', label: '₹30,000 - ₹50,000', tier: 'budget' },
  { value: '50k_to_80k', label: '₹50,000 - ₹80,000', tier: 'mid-range' },
  { value: '80k_to_1_2l', label: '₹80,000 - ₹1,20,000', tier: 'performance' },
  { value: 'above_1_2l', label: 'Above ₹1,20,000', tier: 'premium' },
] as const;

export const URGENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediately', description: 'Ready to buy now' },
  { value: 'within_month', label: 'Within a month', description: 'Soon but not urgent' },
  { value: 'within_3_months', label: 'Within 3 months', description: 'Planning ahead' },
  { value: 'exploring', label: 'Just exploring', description: 'No timeline yet' },
] as const;

export const FUTURE_ACTIVITIES = [
  'Learning programming or coding',
  'Starting freelance work',
  'Video editing / YouTube',
  'Graphic design / UI design',
  'Gaming',
  'Online courses / certifications',
  'Starting a business',
  'Remote work / WFH',
  'Photography / photo editing',
  'Music production',
  'Data science / machine learning',
  'Stock trading / investing',
  'Starting a YouTube channel',
  'E-commerce business',
  'Other',
] as const;

// ============================================
// NEW ENHANCED OPTIONS
// ============================================

export const AGE_OPTIONS = [
  { value: 'under_18', label: 'Under 18' },
  { value: '18_25', label: '18-25 years' },
  { value: '26_35', label: '26-35 years' },
  { value: '36_45', label: '36-45 years' },
  { value: '46_55', label: '46-55 years' },
  { value: 'above_55', label: 'Above 55 years' },
] as const;

export const CURRENT_DEVICE_TYPE_OPTIONS = [
  { value: 'none', label: 'No device currently', icon: '❌' },
  { value: 'laptop', label: 'Laptop', icon: '💻' },
  { value: 'desktop', label: 'Desktop', icon: '🖥️' },
  { value: 'both', label: 'Both Laptop & Desktop', icon: '🔄' },
] as const;

export const CURRENT_DEVICE_AGE_OPTIONS = [
  { value: 'less_than_1', label: 'Less than 1 year' },
  { value: '1_2_years', label: '1-2 years' },
  { value: '3_4_years', label: '3-4 years' },
  { value: '5_plus_years', label: '5+ years' },
] as const;

export const DEVICE_CONDITION_OPTIONS = [
  { value: 'no_device', label: 'No current device', description: 'First time buyer' },
  { value: 'working_fine', label: 'Working fine', description: 'Just want an upgrade' },
  { value: 'slow_laggy', label: 'Slow / Laggy', description: 'Performance issues' },
  { value: 'broken', label: 'Broken / Not working', description: 'Need replacement urgently' },
  { value: 'outdated', label: 'Outdated', description: 'Too old for current needs' },
] as const;

export const PAIN_POINTS_OPTIONS = [
  'Too slow for my work',
  'Battery drains quickly',
  'Heating issues',
  'Storage always full',
  'Screen too small',
  'Heavy to carry',
  'Frequent crashes',
  'Cannot run required software',
  'Poor display quality',
  'Keyboard/trackpad issues',
  'No specific issues',
] as const;

export const OS_PREFERENCE_OPTIONS = [
  { value: 'windows', label: 'Windows', description: 'Most compatible, familiar' },
  { value: 'mac', label: 'macOS', description: 'Apple ecosystem, creative work' },
  { value: 'linux', label: 'Linux', description: 'Development, servers' },
  { value: 'no_preference', label: 'No preference', description: 'Open to suggestions' },
] as const;

export const PORTABILITY_OPTIONS = [
  { value: 'very_important', label: 'Very Important', description: 'Travel frequently' },
  { value: 'somewhat_important', label: 'Somewhat Important', description: 'Occasional travel' },
  { value: 'not_important', label: 'Not Important', description: 'Mostly stationary' },
] as const;

export const SCREEN_SIZE_OPTIONS = [
  { value: 'small_13', label: '13" or smaller', description: 'Ultra-portable' },
  { value: 'medium_14_15', label: '14" - 15.6"', description: 'Balanced' },
  { value: 'large_16_plus', label: '16" or larger', description: 'Maximum screen' },
  { value: 'no_preference', label: 'No preference', description: 'You decide' },
] as const;

export const STORAGE_NEEDS_OPTIONS = [
  { value: 'basic_256gb', label: '256 GB', description: 'Basic usage, cloud storage' },
  { value: 'moderate_512gb', label: '512 GB', description: 'Moderate files, some media' },
  { value: 'heavy_1tb', label: '1 TB', description: 'Lots of files, videos, games' },
  { value: 'very_heavy_2tb_plus', label: '2 TB+', description: 'Professional media work' },
] as const;

export const BRAND_OPTIONS = [
  'HP', 'Dell', 'Lenovo', 'ASUS', 'Acer', 'Apple', 'MSI', 
  'Samsung', 'Microsoft', 'LG', 'Gigabyte', 'Razer', 'No preference'
] as const;

export const PRIORITY_FEATURES = [
  'Long battery life',
  'Lightweight / Portable',
  'Best performance',
  'Good display quality',
  'Build quality / Durability',
  'Good keyboard',
  'Value for money',
  'Gaming capable',
  'Touch screen',
  'Backlit keyboard',
  'Thunderbolt / USB-C ports',
  'Upgradeable RAM/Storage',
] as const;

export const BUDGET_FLEXIBILITY_OPTIONS = [
  { value: 'strict', label: 'Strict budget', description: 'Cannot exceed' },
  { value: 'flexible_10_percent', label: 'Flexible ±10%', description: 'Slight flexibility' },
  { value: 'flexible_20_percent', label: 'Flexible ±20%', description: 'For right device' },
  { value: 'no_limit', label: 'No strict limit', description: 'Quality over price' },
] as const;

export const EXPECTED_USAGE_YEARS_OPTIONS = [
  { value: '1_2_years', label: '1-2 years', description: 'Short term' },
  { value: '3_4_years', label: '3-4 years', description: 'Medium term' },
  { value: '5_plus_years', label: '5+ years', description: 'Long term investment' },
] as const;

// ============================================
// FORM STEPS
// ============================================

export const FORM_STEPS = [
  { id: 1, key: 'personal', title: 'About You', description: 'Basic information' },
  { id: 2, key: 'currentSetup', title: 'Current Setup', description: 'Your existing device' },
  { id: 3, key: 'usage', title: 'Usage Pattern', description: 'How you use a computer' },
  { id: 4, key: 'technical', title: 'Preferences', description: 'Technical requirements' },
  { id: 5, key: 'future', title: 'Budget & Plans', description: 'Goals and timeline' },
  { id: 6, key: 'review', title: 'Review & Pay', description: 'Confirm and proceed' },
] as const;

// ============================================
// ROUTES
// ============================================

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  VERIFY_OTP: '/verify-otp',
  INTRO: '/intro',
  FORM: '/form',
  REVIEW: '/review',
  PAYMENT: '/payment',
  SUCCESS: '/success',
  FAILURE: '/failure',
  TERMS: '/terms',
  PRIVACY: '/privacy',
} as const;

// ============================================
// ERROR MESSAGES
// ============================================

export const ERROR_MESSAGES = {
  GENERIC: 'Something went wrong. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  AUTH_FAILED: 'Authentication failed. Please try again.',
  OTP_EXPIRED: 'OTP has expired. Please request a new one.',
  OTP_INVALID: 'Invalid OTP. Please check and try again.',
  PAYMENT_FAILED: 'Payment could not be processed. Your details are saved.',
  SESSION_EXPIRED: 'Your session has expired. Please login again.',
} as const;

// ============================================
// SUCCESS MESSAGES
// ============================================

export const SUCCESS_MESSAGES = {
  OTP_SENT: 'OTP sent to your email. Please check your inbox.',
  LOGIN_SUCCESS: 'Welcome! Let\'s find the right device for you.',
  PAYMENT_SUCCESS: 'Payment successful! We\'re preparing your recommendation.',
  FORM_SAVED: 'Your progress has been saved.',
} as const;
